/**
 * Robust calculation utilities for Resource Budget tables
 *
 * This module provides bulletproof calculation logic that handles all edge cases
 * and ensures calculations are always correct.
 */

export interface BudgetItem {
  no_of_days: number
  hours_per_day: number
  hours_per_month: number
  amount_per_hour: number
  production: number
  compensation_percent: number
  compensation_amount: number
  wages_per_hour: number
  wages_per_month: number
  [key: string]: any
}

export interface AggregatedTotals {
  no_of_days: number
  hours_per_day: number
  hours_per_month: number
  amount_per_hour: number
  production: number
  compensation_percent: number
  compensation_amount: number
  wages_per_hour: number
  wages_per_month: number
}

/**
 * Safely parse a value to number, handling all edge cases
 */
export function safeParseFloat(value: any): number {
  if (value === null || value === undefined || value === "") return 0
  const parsed = parseFloat(value)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Round to 2 decimal places safely
 */
export function roundTo2Decimals(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * Calculate individual row values for a budget item
 * This ensures all calculated fields are correct
 */
export function calculateIndividualRow(item: BudgetItem): BudgetItem {
  const noOfDays = safeParseFloat(item.no_of_days)
  const hoursPerDay = safeParseFloat(item.hours_per_day)
  const amountPerHour = safeParseFloat(item.amount_per_hour)
  const compensationPercent = safeParseFloat(item.compensation_percent)
  const wagesPerHour = safeParseFloat(item.wages_per_hour)

  // Calculate hours per month
  const hoursPerMonth = roundTo2Decimals(noOfDays * hoursPerDay)

  // Calculate production
  const production = roundTo2Decimals(hoursPerMonth * amountPerHour)

  // Calculate compensation amount
  const compensationAmount = roundTo2Decimals(
    (production * compensationPercent) / 100,
  )

  // Calculate wages per month
  const wagesPerMonth = roundTo2Decimals(hoursPerMonth * wagesPerHour)

  return {
    ...item,
    no_of_days: noOfDays,
    hours_per_day: hoursPerDay,
    hours_per_month: hoursPerMonth,
    amount_per_hour: amountPerHour,
    production: production,
    compensation_percent: compensationPercent,
    compensation_amount: compensationAmount,
    wages_per_hour: wagesPerHour,
    wages_per_month: wagesPerMonth,
  }
}

/**
 * Calculate aggregated totals for multiple budget items
 * This handles all the complex aggregation logic correctly
 * Uses actual values from items (preserving backend compensation values)
 */
export function calculateAggregatedTotals(
  items: BudgetItem[],
): AggregatedTotals {
  if (!items || items.length === 0) {
    return {
      no_of_days: 0,
      hours_per_day: 0,
      hours_per_month: 0,
      amount_per_hour: 0,
      production: 0,
      compensation_percent: 0,
      compensation_amount: 0,
      wages_per_hour: 0,
      wages_per_month: 0,
    }
  }

  // Use items as-is (they already have correct values from backend or user edits)
  // Only ensure hours_per_month, production, wages_per_month are calculated
  const calculatedItems = items.map((item) => {
    const noOfDays = safeParseFloat(item.no_of_days)
    const hoursPerDay = safeParseFloat(item.hours_per_day)
    const amountPerHour = safeParseFloat(item.amount_per_hour)
    const wagesPerHour = safeParseFloat(item.wages_per_hour)
    
    // Preserve compensation values as-is
    const compensationPercent = safeParseFloat(item.compensation_percent)
    const compensationAmount = safeParseFloat(item.compensation_amount)

    // Only calculate derived fields
    const hoursPerMonth = roundTo2Decimals(noOfDays * hoursPerDay)
    const production = roundTo2Decimals(hoursPerMonth * amountPerHour)
    const wagesPerMonth = roundTo2Decimals(hoursPerMonth * wagesPerHour)

    return {
      ...item,
      no_of_days: noOfDays,
      hours_per_day: hoursPerDay,
      hours_per_month: hoursPerMonth,
      amount_per_hour: amountPerHour,
      production: production,
      compensation_percent: compensationPercent,
      compensation_amount: compensationAmount,
      wages_per_hour: wagesPerHour,
      wages_per_month: wagesPerMonth,
    }
  })

  // Sum fields that should be summed
  const totalNoOfDays = calculatedItems.reduce(
    (sum, item) => sum + item.no_of_days,
    0,
  )
  const totalHoursPerDay = calculatedItems.reduce(
    (sum, item) => sum + item.hours_per_day,
    0,
  )
  const totalHoursPerMonth = calculatedItems.reduce(
    (sum, item) => sum + item.hours_per_month,
    0,
  )
  const totalAmountPerHour = calculatedItems.reduce(
    (sum, item) => sum + item.amount_per_hour,
    0,
  )
  const totalProduction = calculatedItems.reduce(
    (sum, item) => sum + item.production,
    0,
  )
  const totalCompensationAmount = calculatedItems.reduce(
    (sum, item) => sum + item.compensation_amount,
    0,
  )
  const totalWagesPerHour = calculatedItems.reduce(
    (sum, item) => sum + item.wages_per_hour,
    0,
  )
  const totalWagesPerMonth = calculatedItems.reduce(
    (sum, item) => sum + item.wages_per_month,
    0,
  )

  let compensationPercent = 0
  if (totalProduction > 0) {
    compensationPercent = roundTo2Decimals(
      (totalCompensationAmount / totalProduction) * 100,
    )
  }

  return {
    no_of_days: roundTo2Decimals(totalNoOfDays),
    hours_per_day: roundTo2Decimals(totalHoursPerDay),
    hours_per_month: roundTo2Decimals(totalHoursPerMonth),
    amount_per_hour: roundTo2Decimals(totalAmountPerHour),
    production: roundTo2Decimals(totalProduction),
    compensation_percent: compensationPercent,
    compensation_amount: roundTo2Decimals(totalCompensationAmount),
    wages_per_hour: roundTo2Decimals(totalWagesPerHour),
    wages_per_month: roundTo2Decimals(totalWagesPerMonth),
  }
}

/**
 * Process budget data from backend - preserve backend values as-is
 * Only calculate derived fields that are always calculated (hours_per_month, production, wages_per_month)
 * DO NOT recalculate compensation_amount or compensation_percent - preserve what backend sends
 */
export function processBudgetData(budgetData: any[]): any[] {
  if (!budgetData || !Array.isArray(budgetData)) {
    return []
  }

  return budgetData.map((item) => {
    // Parse numeric fields but preserve backend values for compensation_amount and compensation_percent
    const noOfDays = safeParseFloat(item.no_of_days)
    const hoursPerDay = safeParseFloat(item.hours_per_day)
    const amountPerHour = safeParseFloat(item.amount_per_hour)
    const wagesPerHour = safeParseFloat(item.wages_per_hour)
    
    // PRESERVE backend values for compensation - don't recalculate
    const compensationPercent = safeParseFloat(item.compensation_percent)
    const compensationAmount = safeParseFloat(item.compensation_amount)
    const backendWagesPerMonth = safeParseFloat(item.wages_per_month)

    // Only calculate derived fields that are always calculated
    const hoursPerMonth = roundTo2Decimals(noOfDays * hoursPerDay)
    const production = roundTo2Decimals(hoursPerMonth * amountPerHour)
    const wagesPerMonth = backendWagesPerMonth > 0 
      ? backendWagesPerMonth 
      : roundTo2Decimals(hoursPerMonth * wagesPerHour)

    return {
      ...item,
      no_of_days: noOfDays,
      hours_per_day: hoursPerDay,
      hours_per_month: hoursPerMonth,
      amount_per_hour: amountPerHour,
      production: production,
      // Preserve backend values - don't recalculate
      compensation_percent: compensationPercent,
      compensation_amount: compensationAmount,
      wages_per_hour: wagesPerHour,
      wages_per_month: wagesPerMonth,
    }
  })
}

/**
 * Validate that calculations are correct
 * This can be used for debugging
 */
export function validateCalculations(item: BudgetItem): boolean {
  const calculated = calculateIndividualRow(item)

  const hoursPerMonthCorrect =
    Math.abs(
      calculated.hours_per_month -
        calculated.no_of_days * calculated.hours_per_day,
    ) < 0.01
  const productionCorrect =
    Math.abs(
      calculated.production -
        calculated.hours_per_month * calculated.amount_per_hour,
    ) < 0.01
  const compensationCorrect =
    Math.abs(
      calculated.compensation_amount -
        (calculated.production * calculated.compensation_percent) / 100,
    ) < 0.01
  const wagesCorrect =
    Math.abs(
      calculated.wages_per_month -
        calculated.hours_per_month * calculated.wages_per_hour,
    ) < 0.01

  return (
    hoursPerMonthCorrect &&
    productionCorrect &&
    compensationCorrect &&
    wagesCorrect
  )
}
